import React, { useMemo } from 'react';

/**
 * 4-tile thumbnail synthesized from a dashboard's items array. Each cell
 * picks a representative item and renders a tiny mock of its visualization
 * (no live SQL — purely a hint that this dashboard contains charts).
 *
 * Accepts an optional `seed` (typically the dashboard id) so each card
 * gets a deterministic-but-different look: every tile (bars, value,
 * line, table, donut, area, …) varies in shape, content and accent.
 */
const PRIMARY = 'var(--theme-primary, #2451b8)';
const VALUE_HINTS = ['#', '%', '$•', '123', '4.2k', '↑12%', '↓3%', '98', '$12k', '+5%'];

function hashSeed(s) {
  if (s == null) return 1;
  let h = 0;
  const str = String(s);
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

// Mulberry32-style deterministic PRNG so every tile can pull values from
// a single seeded stream.
function makeRng(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function DashboardThumbnail({ items = [], seed }) {
  const seedNum = useMemo(() => hashSeed(seed ?? 0), [seed]);
  const accent = PRIMARY;

  const tiles = useMemo(() => buildTiles(items, seedNum), [items, seedNum]);

  return (
    <div className={`dhp-template-thumb${tiles.empty ? ' empty' : ''}`}>
      {tiles.list.map((kind, i) => (
        <div
          key={i}
          className={`tile ${kind}`}
          style={kind === 'value' ? { color: accent } : undefined}
        >
          {renderTile(kind, seedNum + i * 17 + 1, accent)}
        </div>
      ))}
    </div>
  );
}

/**
 * Pick 4 tile kinds deterministically. We bias toward the item types
 * the dashboard actually contains (chart/metric/table) but always vary
 * the concrete visualization, so two different dashboards never share
 * an identical thumbnail.
 */
function buildTiles(items, seedNum) {
  const types = new Set(items.map((i) => i?.item_type).filter(Boolean));
  const chartKinds = ['bars', 'line', 'area', 'donut', 'sparkbars', 'step'];
  const pool = [];
  if (types.has('chart') || types.size === 0) pool.push(...chartKinds);
  if (types.has('metric') || types.size === 0) pool.push('value', 'value');
  if (types.has('table')  || types.size === 0) pool.push('tbl');
  if (pool.length === 0) pool.push(...chartKinds, 'value', 'tbl');

  const rng = makeRng(seedNum);
  const list = [];
  for (let i = 0; i < 4; i++) {
    // Sample with light de-duplication: try a few times to avoid the
    // exact same kind twice in a row.
    let pick = pool[Math.floor(rng() * pool.length)];
    for (let attempt = 0; attempt < 3 && list[list.length - 1] === pick; attempt++) {
      pick = pool[Math.floor(rng() * pool.length)];
    }
    list.push(pick);
  }
  return { list, empty: items.length === 0 };
}

function renderTile(kind, seed, accent) {
  switch (kind) {
    case 'bars':       return <MiniBars seed={seed} accent={accent} />;
    case 'sparkbars':  return <MiniBars seed={seed} accent={accent} sparse />;
    case 'line':       return <MiniLine seed={seed} accent={accent} />;
    case 'step':       return <MiniLine seed={seed} accent={accent} step />;
    case 'area':       return <MiniArea seed={seed} accent={accent} />;
    case 'donut':      return <MiniDonut seed={seed} accent={accent} />;
    case 'value':      return <MiniValue seed={seed} />;
    case 'tbl':        return <MiniTable seed={seed} />;
    default:           return null;
  }
}

function MiniBars({ seed = 1, accent, sparse = false }) {
  const heights = useMemo(() => {
    const rng = makeRng(seed);
    const n = sparse ? 5 : 8;
    return Array.from({ length: n }, () => 20 + Math.floor(rng() * 75));
  }, [seed, sparse]);
  return (
    <>
      {heights.map((h, i) => (
        <span
          key={i}
          className="b"
          style={{
            height: `${h}%`,
            background: accent,
            width: sparse ? '12%' : '7%',
            marginRight: sparse ? 3 : 1,
          }}
        />
      ))}
    </>
  );
}

function MiniLine({ seed = 1, accent, step = false }) {
  const d = useMemo(() => {
    const rng = makeRng(seed);
    const ys = Array.from({ length: 7 }, () => 4 + Math.floor(rng() * 16));
    let path = '';
    ys.forEach((y, i) => {
      const x = i * 10;
      if (i === 0) {
        path = `M${x} ${y}`;
      } else if (step) {
        path += ` L${x} ${ys[i - 1]} L${x} ${y}`;
      } else {
        path += ` L${x} ${y}`;
      }
    });
    return path;
  }, [seed, step]);
  return (
    <svg viewBox="0 0 60 24" preserveAspectRatio="none">
      <path d={d} stroke={accent || 'var(--theme-primary, #2451b8)'} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function MiniArea({ seed = 1, accent }) {
  const { stroke, fill } = useMemo(() => {
    const rng = makeRng(seed);
    const ys = Array.from({ length: 7 }, () => 4 + Math.floor(rng() * 16));
    const line = ys.map((y, i) => `${i === 0 ? 'M' : 'L'}${i * 10} ${y}`).join(' ');
    const area = `${line} L60 24 L0 24 Z`;
    return { stroke: line, fill: area };
  }, [seed]);
  return (
    <svg viewBox="0 0 60 24" preserveAspectRatio="none">
      <path d={fill} fill={accent} fillOpacity="0.25" />
      <path d={stroke} stroke={accent || 'var(--theme-primary, #2451b8)'} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function MiniDonut({ seed = 1, accent }) {
  // Up to 4 segments rotated around a small circle.
  const segs = useMemo(() => {
    const rng = makeRng(seed);
    const raw = Array.from({ length: 3 + Math.floor(rng() * 2) }, () => 0.2 + rng());
    const total = raw.reduce((a, b) => a + b, 0);
    return raw.map((v) => v / total);
  }, [seed]);
  const C = 2 * Math.PI * 8; // circumference for r=8
  let offset = 0;
  // Single-hue palette derived from the theme primary — alternates full
  // opacity / faded so segments stay distinguishable without introducing
  // unrelated colours.
  const opacities = [1, 0.55, 0.8, 0.35];
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" fill="none" stroke="#e5e7eb" strokeWidth="4" />
      {segs.map((s, i) => {
        const len = s * C;
        const dash = `${len} ${C - len}`;
        const el = (
          <circle
            key={i}
            cx="12"
            cy="12"
            r="8"
            fill="none"
            stroke={accent}
            strokeOpacity={opacities[i % opacities.length]}
            strokeWidth="4"
            strokeDasharray={dash}
            strokeDashoffset={-offset}
            transform="rotate(-90 12 12)"
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}

function MiniValue({ seed }) {
  const rng = makeRng(seed);
  const hint = VALUE_HINTS[Math.floor(rng() * VALUE_HINTS.length)];
  return <>{hint}</>;
}

function MiniTable({ seed }) {
  const rows = useMemo(() => {
    const rng = makeRng(seed);
    const n = 3 + Math.floor(rng() * 3); // 3-5 rows
    return Array.from({ length: n }, () => 40 + Math.floor(rng() * 55)); // width %
  }, [seed]);
  return (
    <>
      {rows.map((w, i) => (
        <div key={i} style={{ width: `${w}%`, marginLeft: i % 2 ? 4 : 0 }} />
      ))}
    </>
  );
}
